import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const EMPTY_OVERVIEW = {
  stats: {},
  retentionAnalytics: {},
};

export default function GrowthPulse() {
  const [data, setData] = useState(EMPTY_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/admin/platform/overview');
        setData({
          stats: response.data.stats || {},
          retentionAnalytics: response.data.retentionAnalytics || {},
        });
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not load growth pulse');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const stats = data.stats || {};
  const retention = data.retentionAnalytics || {};

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2.2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.22),transparent_36%),linear-gradient(180deg,#111111,#020202)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.4)] sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#f7d88a]">
            Growth Pulse
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
            Retention and community signals now live on their own page.
          </h1>
          <p className="mt-3 max-w-4xl text-sm leading-7 text-[#cfcfcf]">
            This keeps adoption, community activity, invites, stories, and engagement metrics out of the main dashboard while staying close to the operating context.
          </p>
        </section>

        {error ? (
          <div className="rounded-[1.8rem] border border-red-500/25 bg-red-950/15 px-5 py-4 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-8 text-sm text-[#c8c8c8]">
            Loading growth pulse...
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-6">
              <MetricCard title="New users" value={retention.newUsersLast30d || 0} body="Joined in the last 30 days" />
              <MetricCard title="Day-1 retention" value={`${retention.day1RetentionPercent || 0}%`} body="Returned after first day" />
              <MetricCard title="Day-7 retention" value={`${retention.day7RetentionPercent || 0}%`} body="Still active one week later" />
              <MetricCard title="Active social users" value={stats.activeSocialUsers || 0} body="Users in live social activity" />
              <MetricCard title="Messages 7d" value={stats.socialMessagesLast7d || 0} body="Conversation volume this week" />
              <MetricCard title="Communities" value={stats.totalCommunitySpaces || 0} body="Total groups and spaces" />
            </section>

            <section className="grid gap-6 xl:grid-cols-[1fr,1fr,1fr]">
              <Panel title="Retention path">
                <SignalRow label="First friend within 3 days" value={`${retention.firstFriendWithin3dPercent || 0}%`} />
                <SignalRow label="First message within 7 days" value={`${retention.firstMessageWithin7dPercent || 0}%`} />
                <SignalRow label="Invite acceptance" value={`${retention.inviteAcceptanceRate || stats.inviteAcceptanceRate || 0}%`} />
                <SignalRow label="Accepted friend connections" value={stats.acceptedFriendConnections || 0} />
                <SignalRow label="Pending friend requests" value={stats.pendingFriendRequests || 0} />
              </Panel>

              <Panel title="Community activity">
                <SignalRow label="Conversation threads" value={stats.totalSocialConversations || 0} />
                <SignalRow label="Public community spaces" value={stats.publicCommunitySpaces || 0} />
                <SignalRow label="Pending join requests" value={stats.pendingSpaceJoinRequests || 0} />
                <SignalRow label="Creator follows" value={stats.creatorFollows || 0} />
                <SignalRow label="Stories active now" value={stats.activeStoriesNow || 0} />
              </Panel>

              <Panel title="Engagement signals">
                <SignalRow label="Story views in 7 days" value={stats.storyViewsLast7d || 0} />
                <SignalRow label="Story reactions in 7 days" value={stats.storyReactionsLast7d || 0} />
                <SignalRow label="Call invites in 7 days" value={stats.callInvitesLast7d || 0} />
                <SignalRow label="Open support cases" value={stats.openSupportCases || 0} />
                <SignalRow label="Open moderation reports" value={stats.openSocialReports || 0} />
              </Panel>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}

function MetricCard({ title, value, body }) {
  return (
    <div className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{title}</p>
      <p className="mt-3 text-4xl font-black tracking-[-0.04em] text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-[#b4b4b4]">{body}</p>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
      <h3 className="text-xl font-black tracking-[-0.03em] text-white">{title}</h3>
      <div className="mt-5 space-y-3">{children}</div>
    </div>
  );
}

function SignalRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[1.5rem] border border-[#f5b221]/10 bg-black/50 px-4 py-4">
      <p className="text-sm text-[#c7c7c7]">{label}</p>
      <p className="text-lg font-black text-[#f5b221]">{value}</p>
    </div>
  );
}
